import { useCallback } from "react"
import { useThreadContext } from "@/providers/ThreadProvider"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { casesService } from "@/features/cases/services/cases.service"
import { ApiError } from "@/services/api.client"
import { routeHttpError } from "@/features/oio/errors"
import { useGenerationStore } from "@/features/oio/generation.store"
import { SERVICE_REGISTRY, canonicalServiceId } from "@/services/services.registry"

/** OIO per-case keys, written by OIOWorkspace as `<prefix>-<threadId>`. */
const OIO_CASE_KEY_PREFIXES = ["oio-draft", "oio-findings", "oio-allegs", "oio-review", "oio-started"]

/** Remove all per-case client state when a case is deleted, so no stale run dot,
 *  draft, or analysis lingers. Runs for every order type's keys: the caller
 *  only has the thread id, and removing an absent key is a no-op. */
export function purgeCaseClientState(threadId: string): void {
  useGenerationStore.getState().remove(threadId)
  try {
    for (const prefix of OIO_CASE_KEY_PREFIXES) {
      localStorage.removeItem(`${prefix}-${threadId}`)
    }
    // useRewriteStream's run-id latch (rewriteRunIdStorageKey) uses a colon,
    // not a dash, so the list above never matched it and a deleted case's
    // rewrite run id lingered forever.
    localStorage.removeItem(`oio-rewrite-run-id:${threadId}`)

    // OIA keys are named by the OIA workspace, so scan for the registry's
    // prefix instead of listing them: any `oia-<name>-<id>` / `oia-<name>:<id>`
    // key (services.registry caseStorageKey) is purged without editing this
    // file. Only keys ending in THIS thread id match, so global keys and other
    // cases' keys survive. Collect first: removing while iterating shifts the
    // localStorage indices.
    const oiaPrefix = `${SERVICE_REGISTRY.oia.storageKeyPrefix}-`
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key &&
        key.startsWith(oiaPrefix) &&
        (key.endsWith(`-${threadId}`) || key.endsWith(`:${threadId}`))
      ) {
        doomed.push(key)
      }
    }
    for (const key of doomed) localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

const FALLBACK_ERROR_BODY = {
  code: "SERVER_ERROR",
  message: "Something went wrong. Please retry.",
  severity: "fatal",
  retryable: true,
} as const

function handleCaseError(err: unknown): void {
  if (err instanceof ApiError && err.body.severity === "auth") return
  routeHttpError(
    err instanceof ApiError
      ? { ...err.body, retryable: err.body.retryable ?? false }
      : FALLBACK_ERROR_BODY,
    "",
  )
}

export function useCases() {
  const { threads, activeThread, createThread, deleteThread, setActiveThread, refreshThreads } =
    useThreadContext()
  const { session } = useAuth()

  const newCase = useCallback(
    async (serviceId = "oio") => {
      if (!session) throw new Error("Not authenticated")
      const title = `New case — ${new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}`
      try {
        // Always pass the service id explicitly and canonically: createThread
        // falls back to the vestigial ThreadProvider.activeServiceId (which
        // OIOWorkspace sets to "oio_rewrite") when it is omitted, and the case's
        // service id decides its agent, workspace and status poller for life.
        return await createThread(title, canonicalServiceId(serviceId))
      } catch (err) {
        handleCaseError(err)
      }
    },
    [session, createThread]
  )

  const renameCase = useCallback(
    async (threadId: string, newTitle: string) => {
      try {
        await casesService.update(threadId, { title: newTitle })
        await refreshThreads()
      } catch (err) {
        handleCaseError(err)
      }
    },
    [refreshThreads]
  )

  const deleteCase = useCallback(
    async (threadId: string) => {
      try {
        await deleteThread(threadId)
        purgeCaseClientState(threadId)
      } catch (err) {
        handleCaseError(err)
      }
    },
    [deleteThread]
  )

  return {
    cases: threads,
    activeCase: activeThread,
    setActiveCase: setActiveThread,
    newCase,
    deleteCase,
    renameCase,
  }
}
